import {
  useEffect, useRef, useState
} from 'react';
import { connect } from 'react-redux';

import { graphsAutoRefreshTick } from 'store/graphs/actions';
import {
  getLastAutoRefreshAttemptAt,
  getLastDataPointsRequestAt,
} from 'store/graphs/selectors';

const AUTO_REFRESH_INTERVAL = 60000;

type OwnProps = {
  enabled: boolean;
};

type StateProps = {
  lastDataPointsRequestAt: number | null;
  lastAutoRefreshAttemptAt: number | null;
};

type DispatchProps = {
  graphsAutoRefreshTick: () => void;
};

export const GraphsAutoRefresh = ({
  enabled,
  lastDataPointsRequestAt,
  lastAutoRefreshAttemptAt,
  graphsAutoRefreshTick: dispatchAutoRefreshTick,
}: OwnProps & StateProps & DispatchProps) => {
  const enabledAtRef = useRef<number | null>(enabled ? Date.now() : null);
  const wasEnabledRef = useRef(enabled);
  const [isVisible, setIsVisible] = useState(
    () => document.visibilityState === 'visible'
  );

  useEffect(() => {
    if (enabled && !wasEnabledRef.current) {
      enabledAtRef.current = Date.now();
    }
    if (!enabled) {
      enabledAtRef.current = null;
    }
    wasEnabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === 'visible');
    };

    handleVisibilityChange();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled]);

  useEffect(() => {
    if (
      !enabled ||
      !isVisible ||
      lastDataPointsRequestAt === null ||
      enabledAtRef.current === null
    ) {
      return undefined;
    }

    const deadline = Math.max(
      lastDataPointsRequestAt,
      lastAutoRefreshAttemptAt || 0,
      enabledAtRef.current
    ) + AUTO_REFRESH_INTERVAL;
    const delay = Math.max(0, deadline - Date.now());

    if (delay === 0) {
      dispatchAutoRefreshTick();
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      if (document.visibilityState === 'visible') {
        dispatchAutoRefreshTick();
      } else {
        setIsVisible(false);
      }
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [
    dispatchAutoRefreshTick,
    enabled,
    isVisible,
    lastAutoRefreshAttemptAt,
    lastDataPointsRequestAt,
  ]);

  return null;
};

const mapStateToProps = (state: any) => ({
  lastDataPointsRequestAt: getLastDataPointsRequestAt(state),
  lastAutoRefreshAttemptAt: getLastAutoRefreshAttemptAt(state),
});

export default connect(mapStateToProps, { graphsAutoRefreshTick })(GraphsAutoRefresh);
